import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { SesionService } from '@intelishs-group/session-core';
import type {
  PaymentComplementBulkGroupStatusDto,
  PaymentComplementBulkLogItemDto,
} from '@schemas/invoices';
import { InvoicesService } from './invoices-service';

/** Estado del seguimiento de una tanda. */
export type EstadoTanda = 'siguiendo' | 'terminado' | 'timeout';

/**
 * Metadatos de una tanda de complementos de pago masivos. Es lo que se persiste
 * en `localStorage`: deliberadamente NO incluye los renglones de bitácora, que
 * son pesados y siempre se pueden volver a pedir al backend con el `groupId`.
 */
export interface TandaComplementos {
  /** `groupid` enviado en cada solicitud del lote. Llave del seguimiento. */
  groupId: string;
  /** Solicitudes que sí se alcanzaron a encolar en este lote. */
  enviados: number;
  /** Momento en que se lanzó el lote, en ISO 8601. */
  iniciadaEn: string;
  /** Estado del sondeo desde la perspectiva del cliente. */
  estado: EstadoTanda;
  /** Último resumen de conteos recibido del backend. */
  resumen: PaymentComplementBulkGroupStatusDto | null;
  /** Motivo por el que se dejó de sondear, cuando el cierre no fue normal. */
  aviso: string | null;
  /** `true` cuando el usuario ya abrió el detalle tras el cierre. */
  vista: boolean;
}

/** Página de bitácora de una tanda, tal como se guarda en memoria. */
interface RegistrosTanda {
  items: PaymentComplementBulkLogItemDto[];
  totalItems: number;
}

/** Severidad visual derivada del estatus de un renglón de bitácora. */
export type SeveridadRegistro = 'exito' | 'error' | 'proceso';

/** Primera consulta rápida, para que el panel no se quede vacío 5 segundos. */
const PRIMER_INTERVALO_MS = 1_500;

/** Cadencia normal del sondeo con la pestaña visible. */
const INTERVALO_VISIBLE_MS = 5_000;

/** Cadencia con la pestaña oculta: nadie está mirando, no hay que machacar el API. */
const INTERVALO_OCULTO_MS = 20_000;

/** Tiempo máximo que el cliente sondea una tanda antes de rendirse. */
const TOPE_SONDEO_MS = 5 * 60 * 1_000;

/** Fallos consecutivos de red tolerados antes de abandonar el sondeo. */
const MAX_FALLOS_SEGUIDOS = 3;

/**
 * Renglones que se piden en cada vuelta del sondeo. Se queda corto a propósito:
 * lo que el bucle necesita para decidir si sigue es el RESUMEN, que cuenta la
 * tanda completa sin importar la página, y traer cientos de renglones cada cinco
 * segundos sería desperdicio.
 */
const PAGE_SIZE = 100;

/**
 * Renglones que se piden cuando el usuario abre el detalle. Es el tope que acepta
 * el endpoint (`pageSize` entre 1 y 1000, validado en `FactureService`), y cubre
 * de una sola vez cualquier tanda de ese tamaño.
 *
 * Importa porque la bitácora viene ordenada por PK —el orden de encolado, el
 * único estable entre sondeos— y no por severidad: con una sola página de 100,
 * los errores de una tanda grande simplemente no venían, y el panel mostraba una
 * zona de errores vacía mientras el resumen decía que había errores.
 */
const PAGE_SIZE_DETALLE = 1000;

/** Tandas que se conservan en el mini-histórico local. */
const MAX_TANDAS = 5;

/** Prefijo de la llave de persistencia; se segmenta por usuario. */
const STORAGE_KEY_BASE = 'bulk_tandas';

const AVISO_TIMEOUT =
  'La tanda sigue procesándose en el servidor. Te avisaremos por notificación al terminar.';
const AVISO_SIN_CONEXION =
  'No se pudo consultar el avance. El proceso continúa en el servidor y te avisaremos por notificación.';

/**
 * Seguimiento de las tandas de complementos de pago masivos.
 *
 * `PaymentSupplementBulk` solo encola: el timbrado ocurre después, de forma
 * asíncrona. Este servicio sondea `payment-supplements/bulk-groups` con el
 * `groupid` del lote para exponer el avance y el resultado por renglón.
 *
 * Vive a nivel de aplicación (`providedIn: 'root'`) a propósito: la ruta
 * `/cargas` es lazy y su componente se destruye al navegar, así que si el sondeo
 * viviera en la página se cortaría al salir. Al vivir aquí, la página es solo una
 * vista del estado y el usuario puede irse a otro módulo sin perder el avance. Se
 * instancia al arrancar la app porque el chip del header lo inyecta.
 *
 * No emite avisos propios al terminar: el backend ya manda una notificación por
 * grupo, que aterriza en la campanita del header. Duplicarla aquí sería ruido.
 */
@Injectable({ providedIn: 'root' })
export class ComplementosBulkTrackingService implements OnDestroy {
  private readonly invoicesService = inject(InvoicesService);
  private readonly sesionPlataforma = inject(SesionService);

  /** Tandas conocidas, de la más reciente a la más antigua. */
  private readonly tandasState = signal<TandaComplementos[]>([]);
  /** `groupId` de la tanda que se está mostrando en el panel. */
  private readonly seleccionadaId = signal<string | null>(null);
  /** Bitácoras en memoria, por `groupId`. No se persisten. */
  private readonly registros = signal<Record<string, RegistrosTanda>>({});
  /** `true` mientras se piden los renglones de una tanda del histórico. */
  private readonly cargandoRegistrosState = signal(false);
  /** Error al pedir los renglones de la tanda seleccionada. */
  private readonly errorRegistrosState = signal<string | null>(null);

  /**
   * Token del sondeo en curso. Cada `iniciar` lo incrementa, de modo que un
   * bucle viejo se detiene solo al detectar que ya no es el vigente (evita que
   * dos tandas se pisen el estado).
   */
  private token = 0;

  /**
   * Resolutor de la espera en curso, para poder acortarla. Solo hay uno porque
   * solo se sigue una tanda a la vez.
   */
  private despertar: (() => void) | null = null;

  /**
   * Al volver a la pestaña se acorta la espera larga del modo oculto: si el
   * usuario regresa a mirar, no debe esperar hasta 20 s por datos frescos.
   */
  private readonly onVisibilityChange = (): void => {
    if (!document.hidden) this.despertar?.();
  };

  /** Tandas conocidas, de la más reciente a la más antigua. */
  readonly tandas = this.tandasState.asReadonly();

  /** Tanda más reciente: la única que puede estar sondeándose. */
  readonly tandaActiva = computed<TandaComplementos | null>(() => this.tandasState()[0] ?? null);

  /** Tanda que el panel debe mostrar (la seleccionada o, por defecto, la activa). */
  readonly tandaSeleccionada = computed<TandaComplementos | null>(() => {
    const id = this.seleccionadaId();
    if (!id) return this.tandaActiva();
    return this.tandasState().find((tanda) => tanda.groupId === id) ?? this.tandaActiva();
  });

  /** Renglones de bitácora de la tanda mostrada. */
  readonly registrosSeleccionados = computed<PaymentComplementBulkLogItemDto[]>(() => {
    const tanda = this.tandaSeleccionada();
    if (!tanda) return [];
    return this.registros()[tanda.groupId]?.items ?? [];
  });

  /** Total de renglones que el backend reporta para la tanda mostrada. */
  readonly totalRegistrosSeleccionados = computed<number>(() => {
    const tanda = this.tandaSeleccionada();
    if (!tanda) return 0;
    return this.registros()[tanda.groupId]?.totalItems ?? 0;
  });

  /** Tandas distintas a la mostrada, para el mini-histórico. */
  readonly otrasTandas = computed<TandaComplementos[]>(() => {
    const actual = this.tandaSeleccionada()?.groupId;
    return this.tandasState().filter((tanda) => tanda.groupId !== actual);
  });

  readonly cargandoRegistros = this.cargandoRegistrosState.asReadonly();
  readonly errorRegistros = this.errorRegistrosState.asReadonly();

  /**
   * `true` cuando el chip del header debe mostrarse: mientras la tanda activa se
   * sigue, o cuando ya cerró y el usuario todavía no ha visto el resultado.
   */
  readonly chipVisible = computed<boolean>(() => {
    const tanda = this.tandaActiva();
    if (!tanda) return false;
    return tanda.estado === 'siguiendo' || !tanda.vista;
  });

  /** Renglones cerrados de la tanda activa (completados + con error). */
  readonly cerradosActiva = computed<number>(() => cerradosDe(this.tandaActiva()));

  /** Avance 0-100 de la tanda activa, medido contra las solicitudes enviadas. */
  readonly porcentajeActiva = computed<number>(() => porcentajeDe(this.tandaActiva()));

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.rehidratar();
  }

  ngOnDestroy(): void {
    this.detener();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /**
   * Registra una tanda recién enviada y arranca su seguimiento. La tanda queda
   * como la activa y sustituye el sondeo anterior si había uno (solo se sigue una
   * a la vez; la anterior termina igual en el servidor y avisa por notificación).
   *
   * @param groupId `groupid` enviado en cada solicitud del lote.
   * @param enviados Solicitudes que sí se encolaron.
   */
  iniciar(groupId: string, enviados: number): void {
    if (!groupId || enviados <= 0) return;

    const tanda: TandaComplementos = {
      groupId,
      enviados,
      iniciadaEn: new Date().toISOString(),
      estado: 'siguiendo',
      resumen: null,
      aviso: null,
      vista: false,
    };

    this.tandasState.update((tandas) =>
      [tanda, ...tandas.filter((t) => t.groupId !== groupId)].slice(0, MAX_TANDAS),
    );
    this.seleccionadaId.set(groupId);
    this.errorRegistrosState.set(null);
    this.persistir();

    void this.sondear(groupId, enviados);
  }

  /**
   * Muestra una tanda del histórico en el panel. Si no hay renglones en memoria
   * los pide al backend (una sola vez); los renglones no se persisten justamente
   * porque siempre se pueden recuperar con el `groupId`.
   *
   * @param groupId Tanda a mostrar.
   */
  seleccionar(groupId: string): void {
    if (!this.tandasState().some((tanda) => tanda.groupId === groupId)) return;

    this.seleccionadaId.set(groupId);
    this.errorRegistrosState.set(null);
    if (!this.detalleCompleto(groupId)) void this.cargarRegistros(groupId);
  }

  /**
   * Se asegura de tener la bitácora completa de la tanda que se está mostrando.
   *
   * La llama quien abre el detalle: durante el sondeo solo se guarda la primera
   * página, que en una tanda grande deja fuera justamente los renglones que el
   * usuario viene a ver.
   */
  asegurarDetalleCompleto(): void {
    const tanda = this.tandaSeleccionada();
    if (!tanda || this.detalleCompleto(tanda.groupId)) return;

    void this.cargarRegistros(tanda.groupId);
  }

  /**
   * `true` cuando la bitácora en memoria cubre todos los renglones que el backend
   * reporta para esa tanda.
   *
   * @param groupId Tanda a revisar.
   */
  private detalleCompleto(groupId: string): boolean {
    const guardados = this.registros()[groupId];
    return !!guardados && guardados.items.length >= guardados.totalItems;
  }

  /**
   * Marca la tanda activa como vista, para que el chip del header desaparezca
   * cuando el usuario ya revisó el resultado. Solo aplica a tandas cerradas: si
   * el sondeo sigue, el chip debe permanecer.
   */
  marcarVista(): void {
    const tanda = this.tandaActiva();
    if (!tanda || tanda.estado === 'siguiendo' || tanda.vista) return;

    this.actualizarTanda(tanda.groupId, { vista: true });
    this.persistir();
  }

  /** Detiene el sondeo en curso, si hay uno. */
  detener(): void {
    this.token++;
  }

  // ---------------------------------------------------------------------------
  // Sondeo
  // ---------------------------------------------------------------------------

  /**
   * Bucle de sondeo de una tanda. Termina cuando el grupo cierra, cuando se
   * agota el tope de tiempo, cuando la red falla de forma sostenida o cuando otro
   * `iniciar` invalida el token.
   *
   * El corte por cierre exige DOS condiciones, por contrato del endpoint:
   * `sinPendientes` solo afirma que los registros ya existentes cerraron, y el
   * servicio no sabe cuántas solicitudes componen la tanda (cada una llegó en su
   * propia petición). Sin comparar además `total >= enviados` se daría por
   * cerrada una tanda de la que apenas llegó una parte a la bitácora.
   *
   * @param groupId Tanda a seguir.
   * @param enviados Solicitudes encoladas, para decidir el cierre.
   */
  private async sondear(groupId: string, enviados: number): Promise<void> {
    const token = ++this.token;
    const limite = Date.now() + TOPE_SONDEO_MS;
    let fallosSeguidos = 0;
    let espera = PRIMER_INTERVALO_MS;

    while (token === this.token) {
      await this.esperar(espera);
      if (token !== this.token) return;

      if (Date.now() > limite) {
        this.cerrar(groupId, 'timeout', AVISO_TIMEOUT);
        return;
      }

      try {
        const seguimiento = await this.invoicesService.paymentSupplements_bulkGroups(
          groupId,
          this.idUsuario(),
          1,
          PAGE_SIZE,
        );
        if (token !== this.token) return;

        fallosSeguidos = 0;
        const resumen = seguimiento?.resumen ?? null;
        this.actualizarTanda(groupId, { resumen });
        this.guardarRegistros(groupId, seguimiento?.registros ?? null);

        if (resumen?.sinPendientes && (resumen.total ?? 0) >= enviados) {
          this.cerrar(groupId, 'terminado', null);
          return;
        }
      } catch {
        if (token !== this.token) return;
        fallosSeguidos++;
        if (fallosSeguidos >= MAX_FALLOS_SEGUIDOS) {
          this.cerrar(groupId, 'timeout', AVISO_SIN_CONEXION);
          return;
        }
      }

      espera = this.intervaloActual();
    }
  }

  /**
   * Cierra el seguimiento de una tanda y lo persiste.
   *
   * @param groupId Tanda a cerrar.
   * @param estado Estado final.
   * @param aviso Motivo del cierre cuando no fue normal.
   */
  private cerrar(groupId: string, estado: EstadoTanda, aviso: string | null): void {
    this.actualizarTanda(groupId, { estado, aviso });
    this.persistir();

    // Solo en el cierre normal: el sondeo dejó en memoria la primera página, y
    // esta es la última oportunidad de completarla sin que el usuario espere al
    // abrir el detalle. En un cierre por tope de tiempo o por red no se insiste
    // —la tanda sigue abierta en el servidor, o no hay con quién hablar—.
    if (estado === 'terminado' && !this.detalleCompleto(groupId)) {
      void this.cargarRegistros(groupId);
    }
  }

  /** Cadencia del siguiente sondeo según si la pestaña está visible. */
  private intervaloActual(): number {
    const oculta = typeof document !== 'undefined' && document.hidden;
    return oculta ? INTERVALO_OCULTO_MS : INTERVALO_VISIBLE_MS;
  }

  /**
   * Espera antes del siguiente sondeo. La espera puede acortarse desde
   * {@link onVisibilityChange} cuando el usuario vuelve a la pestaña.
   *
   * @param ms Milisegundos a esperar.
   */
  private esperar(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.despertar = null;
        resolve();
      }, ms);

      this.despertar = () => {
        clearTimeout(timer);
        this.despertar = null;
        resolve();
      };
    });
  }

  /**
   * Pide la primera página de bitácora de una tanda del histórico.
   * @param groupId Tanda a consultar.
   */
  private async cargarRegistros(groupId: string): Promise<void> {
    this.cargandoRegistrosState.set(true);
    try {
      const seguimiento = await this.invoicesService.paymentSupplements_bulkGroups(
        groupId,
        this.idUsuario(),
        1,
        PAGE_SIZE_DETALLE,
      );
      this.actualizarTanda(groupId, { resumen: seguimiento?.resumen ?? null });
      this.guardarRegistros(groupId, seguimiento?.registros ?? null);
      this.persistir();
    } catch (error) {
      const detalle = error instanceof Error ? error.message : 'Error al consultar la tanda';
      this.errorRegistrosState.set(detalle);
    } finally {
      this.cargandoRegistrosState.set(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Estado
  // ---------------------------------------------------------------------------

  /**
   * Aplica cambios parciales a una tanda por `groupId`, sin tocar las demás.
   *
   * @param groupId Tanda a modificar.
   * @param cambios Propiedades a sobrescribir.
   */
  private actualizarTanda(groupId: string, cambios: Partial<TandaComplementos>): void {
    this.tandasState.update((tandas) =>
      tandas.map((tanda) => (tanda.groupId === groupId ? { ...tanda, ...cambios } : tanda)),
    );
  }

  /**
   * Guarda en memoria la página de bitácora recibida.
   *
   * @param groupId Tanda a la que pertenece.
   * @param pagina Respuesta paginada del endpoint.
   */
  private guardarRegistros(
    groupId: string,
    pagina: {
      items?: PaymentComplementBulkLogItemDto[] | null;
      totalItems?: number;
    } | null,
  ): void {
    if (!pagina) return;
    this.registros.update((mapa) => ({
      ...mapa,
      [groupId]: {
        items: pagina.items ?? [],
        totalItems: pagina.totalItems ?? pagina.items?.length ?? 0,
      },
    }));
  }

  /** Id del usuario en sesión, requerido por el endpoint de seguimiento. */
  private idUsuario(): number {
    return this.sesionPlataforma.usuario()?.id ?? 0;
  }

  // ---------------------------------------------------------------------------
  // Persistencia
  // ---------------------------------------------------------------------------

  /**
   * Llave de `localStorage` segmentada por usuario, para que dos sesiones en el
   * mismo navegador no compartan tandas (mismo criterio que las notificaciones).
   */
  private storageKey(): string {
    const idUsuario = this.idUsuario();
    return idUsuario ? `${STORAGE_KEY_BASE}:${idUsuario}` : STORAGE_KEY_BASE;
  }

  private persistir(): void {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.tandasState()));
    } catch {
      // Cuota llena o almacenamiento bloqueado: el seguimiento en memoria sigue
      // funcionando, solo se pierde al recargar.
    }
  }

  /**
   * Rehidrata el mini-histórico y reanuda el sondeo si la tanda más reciente
   * quedó a medias (por ejemplo tras un F5). Una tanda cuyo tope de tiempo ya
   * expiró no se reanuda: se marca como `timeout` para no sondear en vano.
   */
  private rehidratar(): void {
    const tandas = this.leerPersistido();
    if (tandas.length === 0) return;

    this.tandasState.set(tandas);
    this.seleccionadaId.set(tandas[0].groupId);

    const activa = tandas[0];
    if (activa.estado !== 'siguiendo') return;

    const expirada = Date.now() - new Date(activa.iniciadaEn).getTime() > TOPE_SONDEO_MS;
    if (expirada) {
      this.cerrar(activa.groupId, 'timeout', AVISO_TIMEOUT);
      return;
    }

    void this.sondear(activa.groupId, activa.enviados);
  }

  /**
   * Lee y sanea el histórico persistido. Un payload corrupto o de otra versión
   * se descarta en silencio: es caché, no fuente de verdad.
   *
   * @returns Tandas válidas, como máximo {@link MAX_TANDAS}.
   */
  private leerPersistido(): TandaComplementos[] {
    try {
      const crudo = localStorage.getItem(this.storageKey());
      if (!crudo) return [];

      const parsed: unknown = JSON.parse(crudo);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter((item): item is TandaComplementos => {
          if (typeof item !== 'object' || item === null) return false;
          const tanda = item as Partial<TandaComplementos>;
          return typeof tanda.groupId === 'string' && typeof tanda.enviados === 'number';
        })
        .slice(0, MAX_TANDAS);
    } catch {
      return [];
    }
  }
}

/**
 * Renglones ya cerrados de una tanda: completados más los que fallaron.
 *
 * @param tanda Tanda a medir (`null` cuando no hay ninguna).
 * @returns Cantidad de renglones cerrados; 0 si aún no hay resumen.
 */
export function cerradosDe(tanda: TandaComplementos | null): number {
  const resumen = tanda?.resumen;
  if (!resumen) return 0;
  return (resumen.completados ?? 0) + (resumen.conError ?? 0);
}

/**
 * Avance de una tanda en porcentaje.
 *
 * Se mide contra `enviados` (lo que el cliente encoló), no contra `resumen.total`
 * (lo que el backend ya registró): al principio de la tanda `total` va subiendo y
 * usarlo como denominador mostraría 100% con dos renglones procesados.
 *
 * @param tanda Tanda a medir (`null` cuando no hay ninguna).
 * @returns Porcentaje 0-100.
 */
export function porcentajeDe(tanda: TandaComplementos | null): number {
  if (!tanda || tanda.enviados <= 0) return 0;
  const avance = Math.round((cerradosDe(tanda) / tanda.enviados) * 100);
  return Math.min(100, Math.max(0, avance));
}

/**
 * Deriva la severidad visual de un renglón de bitácora.
 *
 * El catálogo numérico `EstatusTimbradoMasivo` no viaja en los schemas
 * generados, así que se clasifica por `descripcionEstatus` (el texto legible que
 * expone el backend) y, como respaldo, por si el renglón ya tiene `mensaje`: un
 * renglón sin mensaje es uno que todavía no cerró.
 *
 * @param registro Renglón de bitácora.
 * @returns Severidad para pintar icono y color.
 */
export function severidadRegistro(registro: PaymentComplementBulkLogItemDto): SeveridadRegistro {
  const descripcion = (registro.descripcionEstatus ?? '').toLowerCase();
  if (descripcion.includes('error') || descripcion.includes('fall')) return 'error';
  if (descripcion.includes('complet') || descripcion.includes('timbrad')) return 'exito';
  return registro.mensaje ? 'exito' : 'proceso';
}
